import { useNavigationRefreshPulse } from "@/hooks/useHook";
import { BarChartOutlined, DashboardOutlined, EditOutlined, ExperimentOutlined, FileOutlined, HomeOutlined, LoadingOutlined, SettingFilled, SettingOutlined, SmileOutlined, SyncOutlined, TeamOutlined } from "@ant-design/icons";
import { useAppSelector } from "@redux/store";
import { IConfig } from "@type/router";
import { getLevelKeys, getTreeLeaf } from "@utils/common";
import { SiderWrapper } from "@views/MainSider/styled";
import { Menu } from "antd";
import type { SiderProps } from "antd/es/layout/Sider";
import type { ItemType, SubMenuType } from "antd/es/menu/interface";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
const iconMap = {
  HomeOutlined: <HomeOutlined />,
  LoadingOutlined: <LoadingOutlined />,
  SettingFilled: <SettingFilled />,
  SmileOutlined: <SmileOutlined />,
  SyncOutlined: <SyncOutlined />,
  EditOutlined: <EditOutlined />,
  SettingOutlined: <SettingOutlined />,
  FileOutlined: <FileOutlined />,
  ExperimentOutlined: <ExperimentOutlined />,
  BarChartOutlined: <BarChartOutlined />,
  DashboardOutlined: <DashboardOutlined />,
  TeamOutlined: <TeamOutlined />,
};
function IconElement(props: { type: keyof typeof iconMap }) {
  const { type } = props;
  const IconComponent = iconMap[type];
  return IconComponent;
}

export function setMenu(menuConfig: IConfig[], t: (key: string) => string): (SubMenuType | ItemType)[] {
  return menuConfig
    .filter(item => !item.index && item.key !== "__INDEX_REDIRECT__" && !item.hideInMenu)
    .map(item => {
      const { key, label, icon, children } = item;
      // Treat empty children array as a leaf item (no submenu)
      const hasChildren = children && children.length > 0;
      return {
        key,
        label: t(label),
        icon: icon ? <IconElement type={icon as keyof typeof iconMap} /> : undefined,
        children: hasChildren ? setMenu(children, t) : undefined,
      };
    });
}
function MainSider(props: SiderProps & React.RefAttributes<HTMLDivElement>) {
  const { t } = useTranslation();
  // Auto-collapse on tablets: either narrow viewport (≤1024px) OR touch-only
  // device (coarse pointer) regardless of CSS pixel count. KFPIWI tablet
  // reports a 1920px viewport but is touch-only, so width alone misses it.
  const shouldCollapseForDevice = (): boolean => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 1024px), (pointer: coarse)").matches;
  };
  const [collapsed, setCollapsed] = useState(shouldCollapseForDevice);
  const [openKeys, setStateOpenKeys] = useState<string[]>([]);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1024px), (pointer: coarse)");
    const onChange = (e: MediaQueryListEvent) => setCollapsed(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  const [selectKeys, setSelectKeys] = useState<string[]>([]);
  const location = useLocation();
  const navigate = useNavigate();

  const routerData = useAppSelector(state => state.router);

  const menuConfig = routerData.menuConfig[0].children ?? [];
  const levelKeys = getLevelKeys(menuConfig);

  useEffect(() => {
    const data = getTreeLeaf(menuConfig, "path", location.pathname);
    if (data && !selectKeys.includes(data.leaf.key)) {
      onOpenChange(data.parentKey.map(item => item.key));
      setSelectKeys([data.leaf.key]);
    }
  }, [location.pathname]);
  // CALLED WHEN LOCATION CHANGES (including hash for tab changes), but not on initial mount —
  // each page's useRefresh already loads on mount, so pulsing on mount too double-fetches.
  useNavigationRefreshPulse(location);

  function onSelect(select: string[]) {
    setSelectKeys(select);
    const newLocation = getTreeLeaf(menuConfig, "key", select[0]);
    navigate(newLocation?.leaf.path ?? "/");
  }
  function onOpenChange(openKeys: string[]) {
    const currentOpenKey = openKeys.find(key => !openKeys.includes(key));
    if (currentOpenKey !== undefined) {
      const repeatIndex = openKeys.filter(key => key !== currentOpenKey).findIndex(key => levelKeys[key] === levelKeys[currentOpenKey]);
      setStateOpenKeys(
        openKeys
          // remove repeat key
          .filter((_, index) => index !== repeatIndex)
          // remove current level all child
          .filter(key => levelKeys[key] <= levelKeys[currentOpenKey]),
      );
    } else {
      // close
      setStateOpenKeys(openKeys);
    }
  }
  return (
    <SiderWrapper {...props} collapsed={collapsed} collapsible collapsedWidth={48} onCollapse={value => setCollapsed(value)}>
      <Menu theme="dark" mode="inline" selectedKeys={selectKeys} openKeys={openKeys} onOpenChange={onOpenChange} onSelect={e => onSelect(e.selectedKeys)} items={setMenu(menuConfig, t)} />
    </SiderWrapper>
  );
}
export default MainSider;
